import React, { useState, useEffect, useRef } from 'react';
import { motion } from "framer-motion";
import FloatingSkills from './FloatingSkills';
import Footer from './Footer';
import { 
  Github, ExternalLink, Database, Send, BarChart3, 
  Brain, Linkedin, Mail, Award, Terminal, Users, Search, 
  Download, ArrowRight, Menu, X, Briefcase, GraduationCap, Calendar 
} from 'lucide-react';
import emailjs from '@emailjs/browser';

const Portfolio = () => {
  const [typedText, setTypedText] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const form = useRef();
  
  const fullText = "I build and deploy production-grade AI systems.";


  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      setTypedText(fullText.slice(0, index + 1));
      index++;
      if (index === fullText.length) clearInterval(interval);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const sendEmail = (e) => {
    e.preventDefault();
    emailjs.sendForm('service_egkocln', 'template_7yfltxj', form.current, 'ehHOs4mjasVtnI70X')
      .then(() => {
          alert("Message Sent Successfully! 🚀");
          e.target.reset();
      }, (error) => {
          alert("Failed to send. Please try again.");
          console.log(error.text);
      });
  };

  const openModal = (e, project) => {
    e.preventDefault();
    if(project.embedUrl) {
      setSelectedProject(project);
      setIsModalOpen(true);
    } else {
      alert("Case study presentation coming soon! I am currently designing it.");
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const formatDuration = (startStr, endStr) => {
    const startDate = new Date(startStr);
    const endDate = (!endStr || endStr.toLowerCase() === 'present') ? new Date() : new Date(endStr);
    if (isNaN(startDate.getTime())) return "";

    let totalMonths = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth()) + 1;
    if (totalMonths <= 0) totalMonths = 1;

    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    
    let durationArray = [];
    if (years > 0) durationArray.push(`${years} yr${years > 1 ? 's' : ''}`);
    if (months > 0) durationArray.push(`${months} mo${months > 1 ? 's' : ''}`);
    
    return durationArray.join(' ');
  };

  const formatDisplayDate = (startStr, endStr) => {
    const isPresent = !endStr || endStr.toLowerCase() === 'present';
    return `${startStr} - ${isPresent ? 'Present' : endStr}`;
  };

  const journeyData = [
    {
      id: "shabd-group",
      type: "company_group",
      organization: "Shabd",
      link: "https://shabd.tech/",
      location: "Kolkata, West Bengal · On-site",
      totalDuration: "7 mos",
      Icon: Briefcase,
      roles: [
        {
          id: "r1",
          role: "AI Engineer",
          employmentType: "Full-time",
          startDate: "Oct 2026",
          endDate: "",
          isCurrent: true,
          description: "Designing, building, and deploying production AI systems and scalable architectures. Advancing intelligent NLP and speech pipelines that go 'Beyond Language Barriers'."
        },
        {
          id: "r2",
          role: "AI Data Engineer",
          employmentType: "Internship",
          startDate: "Apr 2026",
          endDate: "Sep 2026",
          isCurrent: false,
          description: "Engineered AI-driven data pipelines and machine learning models. Focused on data extraction, scalable architecture, and intelligent processing."
        }
      ]
    },
    {
      id: "btech",
      type: "single",
      role: "B.Tech in Computer Science Engineering (AI & ML)",
      organization: "Brainware University",
      link: "https://www.brainwareuniversity.ac.in/",
      location: "Barasat, West Bengal",
      startDate: "Aug 2022",
      endDate: "July 2026", 
      description: "Graduated with a specialization in Artificial Intelligence and Machine Learning. Focused on core computer science fundamentals, neural networks, and scalable ML systems, bridging theoretical algorithms with real-world production engineering.",
      Icon: GraduationCap,
      isCurrent: false
    },
    {
      id: "intel",
      type: "single",
      role: "Industrial Trainee (AI)",
      organization: "Intel Unnati",
      link: null,
      location: "Remote",
      startDate: "Feb 2025",
      endDate: "Apr 2025", 
      description: "Engineered an AI-powered sentiment analysis and video analytics system leveraging OpenVINO. Focused on deep learning model optimization for edge hardware acceleration.",
      Icon: Briefcase,
      isCurrent: false
    },
    {
      id: "ibm-cert",
      type: "single",
      role: "AI & Cloud Solutions Certification",
      organization: "IBM SkillsBuild",
      link: null,
      location: null,
      startDate: "Aug 2025",
      endDate: "Aug 2025",
      description: "Mastered core AI frameworks, neural networks, and scalable cloud architectures. Deployed cloud-native test applications using IBM Code Engine.",
      Icon: Award,
      isCurrent: false
    }
  ];

  const stats = [
    { num: "10+", label: "Projects Completed" },
    { num: "3+", label: "Years of Coding" },
    { num: "25+", label: "Tech Skills" },
  ];

  const certifications = [
    {
      title: "Intel Unnati Industrial Training 2025",
      issuer: "Intel",
      date: "April 2025",
      points: ["Built AI-powered sentiment analysis system using OpenVINO.", "Optimized deep learning models for Intel hardware acceleration.", "Analyzed customer behavior data to improve decision-making efficiency."],
      link: "#"
    },
    {
      title: "Artificial Intelligence Fundamentals",
      issuer: "IBM SkillsBuild",
      date: "August 2025",
      points: ["Mastered core AI concepts: Neural Networks, Deep Learning, & ML.", "Explored ethical AI frameworks and real-world deployment strategies.", "Applied machine learning algorithms to modern software solutions."],
      link: "https://www.credly.com/badges/3ec663c7-b1c0-4310-9271-5916f5b808a2/public_url" 
    },
    {
      title: "Journey to Cloud: Solution Envisioning",
      issuer: "IBM SkillsBuild",
      date: "August 2025",
      points: ["Designed scalable cloud architectures using IaaS, PaaS, and SaaS models.", "Deployed test pilot applications using IBM Code Engine.", "Implemented Agile & Enterprise Design Thinking for digital transformation."],
      link: "https://www.credly.com/badges/05320164-a4ff-448f-88fb-a6e4c2f60438/public_url" 
    }
  ];

  const projects = [
    {
      title: "Smart Coding Assistant",
      category: "ai",
      description: "An AI-powered coding assistant that helps students prepare for coding interviews using Retrieval-Augmented Generation (RAG), Google Gemini, and LangChain.",
      tags: ["Python", "Streamlit", "RAG", "LangChain", "Google Gemini"],
      embedUrl: "#", 
      links: { tryIt: "https://smart-coding-assistant-cucqzselq3p8jngpgun6a3.streamlit.app/", code: "https://github.com/SubhankarChand/Smart-Coding-Assistant" }
    },
    {
      title: "AI Resume Analyzer",
      category: "ai",
      description: "An AI-powered Resume Analyzer using NLP to automate resume screening. Features skill gap detection, TF-IDF similarity scoring, and automated PDF report generation.",
      tags: ["Python", "Streamlit", "NLP", "Scikit-Learn"],
      embedUrl: "#", 
      links: { tryIt: "https://resume-analyzer-fqrcvl4aztjwtp8acbz8rf.streamlit.app/", code: "https://github.com/SubhankarChand/Resume-Analyzer" }
    },
    {
      title: "Forest Fire Prediction",
      category: "ml",
      description: "Machine learning pipeline using Random Forest to predict forest fires with 90% accuracy based on meteorological data.",
      tags: ["Python", "Scikit-learn", "Pandas"],
      embedUrl: "https://gamma.app/embed/8686ojoyd8pvnm3", 
      links: { tryIt: "#", code: "https://github.com/SubhankarChand" } 
    },
    {
      title: "AI Video Analytics",
      category: "ai",
      description: "Real-time Customer Experience Analytics System for retail. Leverages computer vision to track visitor footfall and emotion.",
      tags: ["OpenCV", "YOLOv8", "Flask"],
      embedUrl: "https://gamma.app/embed/r4myqcr1kjw7xcl", 
      links: { tryIt: "#", code: "https://github.com/SubhankarChand/AI-Powered-Video-Analytics-with-OpenVINO" }
    },
    {
      title: "Library Management System",
      category: "web",
      description: "KitabGhar is a full-featured, web-based library management system built with Python (Flask) and MySQL.",
      tags: ["Python", "Flask", "MySQL"],
      embedUrl: "https://gamma.app/embed/ljzh92euombkrni", 
      links: { tryIt: "#", code: "https://github.com/SubhankarChand/Library-Management-System" }
    }
  ];

  return (
    // Added flex flex-col and min-h-screen here to ensure the footer stays at the very bottom
    <div className="min-h-screen flex flex-col bg-[#0f172a] text-slate-300 font-sans selection:bg-cyan-500 selection:text-white overflow-x-hidden relative">
      
      {isModalOpen && selectedProject && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-sm">
          <div className="relative w-full max-w-5xl bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden flex flex-col h-[85vh] animate-in fade-in zoom-in duration-300">
            <div className="flex justify-between items-center p-4 border-b border-slate-800 bg-slate-900/50">
              <div className="flex items-center gap-3">
                <Brain className="text-cyan-400" size={24} />
                <h3 className="text-xl font-bold text-white tracking-wide">{selectedProject.title} <span className="text-slate-500 font-normal">| Case Study</span></h3>
              </div>
              <button onClick={closeModal} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-all">
                <X size={24} />
              </button>
            </div>
            <div className="flex-grow w-full h-full bg-[#0f172a]">
              <iframe src={selectedProject.embedUrl} className="w-full h-full border-none" allow="fullscreen" title={`${selectedProject.title} Presentation`}></iframe>
            </div>
          </div>
        </div>
      )}

      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-[#0b1329]/80 backdrop-blur-md border-b border-slate-800/50 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="text-xl font-bold text-white tracking-wide">
            Subhankar <span className="text-cyan-400">Chand</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#hero" className="text-sm font-medium text-slate-300 hover:text-cyan-400 transition-colors">Home</a>
            <a href="#skills" className="text-sm font-medium text-slate-300 hover:text-cyan-400 transition-colors">Skills</a>
            <a href="#projects" className="text-sm font-medium text-slate-300 hover:text-cyan-400 transition-colors">Projects</a>
            <a href="#certifications" className="text-sm font-medium text-slate-300 hover:text-cyan-400 transition-colors">Certifications</a>
            {/* CHANGED: button to an anchor tag for smooth scrolling */}
            <a href="#contact" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-medium py-2 px-6 rounded-full shadow-[0_0_15px_rgba(34,211,238,0.3)] hover:shadow-[0_0_25px_rgba(34,211,238,0.6)] transition-all duration-300 inline-block text-center cursor-pointer">
              Contact Me
            </a>
          </div>
          <div className="md:hidden text-white cursor-pointer" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X /> : <Menu />}
          </div>
        </div>
        {isMenuOpen && (
             <div className="md:hidden bg-[#0f172a] border-b border-slate-800 p-4 flex flex-col gap-4 text-center">
                <a href="#hero" onClick={() => setIsMenuOpen(false)}>Home</a>
                <a href="#skills" onClick={() => setIsMenuOpen(false)}>Skills</a>
                <a href="#projects" onClick={() => setIsMenuOpen(false)}>Projects</a>
                <a href="#certifications" onClick={() => setIsMenuOpen(false)}>Certifications</a>
                {/* CHANGED: Added setIsMenuOpen(false) to close menu on mobile when clicked */}
                <a href="#contact" onClick={() => setIsMenuOpen(false)} className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-medium py-2 px-6 rounded-full shadow-[0_0_15px_rgba(34,211,238,0.3)] hover:shadow-[0_0_25px_rgba(34,211,238,0.6)] transition-all duration-300 inline-block text-center cursor-pointer">
                    Contact Me
                </a>
             </div>
        )}
      </nav>

      {/* Main Content Area - Wrapped in flex-grow */}
      <main className="flex-grow w-full">
        {/* Hero Section */}
        <section id="hero" className="pt-32 pb-20 px-6 max-w-6xl mx-auto min-h-screen flex flex-col justify-center">
          <div className="flex flex-col-reverse lg:flex-row items-center justify-between gap-12">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="flex-1 text-center lg:text-left">
              <p className="inline-block px-3 py-1 mb-4 text-xs font-mono font-medium tracking-wider text-cyan-400 uppercase bg-cyan-500/10 rounded-full border border-cyan-500/20">
                AI Engineer | ML Enthusiast
              </p>
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
                Subhankar Chand.
              </h1>
              <div className="min-h-[4rem] sm:min-h-[5rem] mb-6 flex items-center">
                  <h2 className="text-2xl md:text-4xl font-semibold text-slate-300 leading-snug">
                    {typedText}<span className="animate-pulse text-cyan-400 font-normal">|</span>
                  </h2>
              </div>

              <p className="max-w-xl mx-auto lg:mx-0 text-lg text-slate-400 mb-10 leading-relaxed">
                Currently an{" "}
                <a 
                  href="https://shabd.tech/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-cyan-400 font-semibold hover:underline"
                >
                  AI Engineer at Shabd
                </a>. 
                I design and deploy scalable intelligent systems, turning complex models into reliable, real-world software.
              </p>


              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12">
                <a href="/resume.pdf" download="Subhankar_Chand_Resume.pdf" className="flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-full font-bold shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:-translate-y-1 transition-all">
                  <Download size={20} />
                  Download Resume
                </a>
                <a href="#contact" className="flex items-center justify-center gap-2 px-8 py-4 bg-transparent border border-slate-600 text-white rounded-full font-bold hover:border-cyan-400 hover:text-cyan-400 hover:-translate-y-1 transition-all">
                  Let's Connect
                  <ArrowRight size={20} />
                </a>
              </div>

              <div className="flex flex-wrap justify-center lg:justify-start gap-12 border-t border-slate-800/50 pt-8">
                {stats.map((stat, index) => (
                  <div key={index} className="flex flex-col items-center lg:items-start">
                    <h3 className="text-3xl font-bold text-white mb-1">{stat.num}</h3>
                    <p className="text-sm text-slate-500 uppercase tracking-wide font-mono">{stat.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="flex-1 relative">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-cyan-500 blur-[120px] opacity-20 rounded-full"></div>
              <div className="relative w-72 h-72 md:w-96 md:h-96 mx-auto bg-slate-900 rounded-full border-[6px] border-slate-800 overflow-hidden shadow-2xl group">
                <img src="/profile.png" onError={(e) => e.target.src = 'https://api.dicebear.com/9.x/avataaars/svg?seed=Subhankar'} alt="Subhankar Chand" className="w-full h-full object-cover object-top transition-all duration-500"/>
              </div>
  
            {/* Add this floating badge below the image */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-slate-900/95 border border-cyan-500/30 px-4 py-1.5 rounded-full flex items-center gap-2.5 shadow-xl backdrop-blur-md">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-500"></span>
              </span>
              <span className="text-xs font-mono font-medium text-slate-200 whitespace-nowrap">AI Engineer @ Shabd</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Experience & Education Timeline Section */}
      <section id="journey" className="py-24 bg-slate-900/30 border-t border-slate-800">
          <div className="max-w-4xl mx-auto px-6">
            <div className="flex items-center gap-4 mb-16">
              <h2 className="text-3xl font-bold text-white"><span className="text-cyan-400 font-mono">00.</span> My Journey</h2>
              <div className="h-px bg-slate-800 flex-grow"></div>
            </div>

            <div className="relative border-l-2 border-slate-800 ml-4 md:ml-6 space-y-14">
              {journeyData.map((item) => {
                if (item.type === "company_group") {
                  return (
                    <div key={item.id} className="relative pl-8 md:pl-12">
                      {/* Main Node Indicator */}
                      <div className="absolute -left-[11px] top-1.5 h-5 w-5 rounded-full border-4 border-[#0f172a] bg-cyan-500 shadow-[0_0_12px_rgba(34,211,238,0.6)]"></div>
                      
                      {/* Company Header */}
                      <div className="mb-6">
                        <div className="flex flex-wrap items-center gap-3">
                          <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-2xl font-bold text-white hover:text-cyan-400 flex items-center gap-2 transition-colors">
                            {item.organization} <ExternalLink size={18} />
                          </a>
                          <span className="text-xs font-mono text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 px-2.5 py-0.5 rounded-full">
                            Promoted internally
                          </span>
                        </div>
                        {item.location && (
                          <p className="text-slate-500 text-xs mt-1 font-mono uppercase tracking-wide">
                            {item.location} · <span className="text-slate-400">{item.totalDuration}</span>
                          </p>
                        )}
                      </div>

                      {/* Nested Roles (Inner Tree Line) */}
                      <div className="relative border-l-2 border-slate-700/60 ml-2 space-y-8 pl-6 my-4">
                        {item.roles.map((role) => (
                          <div key={role.id} className="relative group">
                            {/* Inner Sub-node Dot */}
                            <div className={`absolute -left-[31px] top-1.5 h-3.5 w-3.5 rounded-full border-2 border-[#0f172a] transition-all
                              ${role.isCurrent ? 'bg-cyan-400 ring-4 ring-cyan-500/20' : 'bg-slate-500 group-hover:bg-cyan-400'}`}>
                            </div>

                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-1 mb-1.5">
                              <div className="flex items-center gap-2">
                                <h3 className="text-lg font-bold text-white">{role.role}</h3>
                                {role.employmentType && (
                                  <span className="text-xs text-slate-400 font-medium">· {role.employmentType}</span>
                                )}
                              </div>
                              <span className={`flex items-center gap-1 text-xs font-mono px-3 py-0.5 rounded-full w-max border 
                                ${role.isCurrent ? 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20' : 'text-slate-400 bg-slate-800 border-slate-700'}`}>
                                <Calendar size={13} />
                                {formatDisplayDate(role.startDate, role.endDate)} · {formatDuration(role.startDate, role.endDate)}
                              </span>
                            </div>

                            <p className="text-slate-400 text-sm leading-relaxed mt-2">
                              {role.description}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                // Standard Single Entry (Education, Trainee, Certifications)
                return (
                  <div key={item.id} className="relative pl-8 md:pl-12 group">
                    <div className="absolute -left-[11px] top-1.5 h-5 w-5 rounded-full border-4 border-[#0f172a] bg-slate-700 group-hover:bg-cyan-400 transition-all"></div>
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-2 gap-2">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <item.Icon size={20} className="text-cyan-400" />
                        {item.role}
                      </h3>
                      <span className="flex items-center gap-1 text-xs font-mono px-3 py-1 rounded-full w-max border text-slate-400 bg-slate-800 border-slate-700">
                        <Calendar size={14} /> 
                        {formatDisplayDate(item.startDate, item.endDate)} · {formatDuration(item.startDate, item.endDate)}
                      </span>
                    </div>
                    <div className="mb-3">
                      <h4 className="text-slate-300 font-medium">{item.organization}</h4>
                      {item.location && <p className="text-slate-500 text-xs mt-1 font-mono uppercase tracking-wide">{item.location}</p>}
                    </div>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Skills Section */}
        <section id="skills" className="py-24 bg-[#0b1329]">
          <div className="max-w-6xl mx-auto px-6">
             <div className="flex items-center gap-4 mb-16">
              <h2 className="text-3xl font-bold text-white">
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent font-mono pr-2">01.</span> 
                Technical Arsenal
              </h2>
              <div className="h-px bg-slate-800 flex-grow"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Card 1: Generative AI & LLMs */}
              <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 hover:border-cyan-500/50 hover:shadow-lg hover:shadow-cyan-900/20 hover:-translate-y-1 transition-all duration-300 group">
                <div className="flex items-center gap-3 mb-6 border-b border-slate-700 pb-4">
                  <div className="p-2.5 bg-cyan-500/10 rounded-lg text-cyan-400 group-hover:scale-110 transition-transform"><Brain size={22}/></div>
                  <h4 className="font-bold text-white text-lg">GenAI & LLMs</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {["LangChain", "RAG", "Google Gemini API", "Transformers", "NLP", "OpenVINO"].map((skill) => (
                    <span key={skill} className="px-3 py-1.5 bg-slate-900 text-slate-300 rounded-lg text-xs font-semibold hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors cursor-default">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {/* Card 2: Machine Learning & CV */}
              <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 hover:border-cyan-500/50 hover:shadow-lg hover:shadow-cyan-900/20 hover:-translate-y-1 transition-all duration-300 group">
                <div className="flex items-center gap-3 mb-6 border-b border-slate-700 pb-4">
                  <div className="p-2.5 bg-cyan-500/10 rounded-lg text-cyan-400 group-hover:scale-110 transition-transform"><BarChart3 size={22}/></div>
                  <h4 className="font-bold text-white text-lg">Machine Learning & CV</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {["Scikit-Learn", "TensorFlow", "OpenCV", "YOLOv8", "Pandas", "NumPy"].map((skill) => (
                    <span key={skill} className="px-3 py-1.5 bg-slate-900 text-slate-300 rounded-lg text-xs font-semibold hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors cursor-default">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {/* Card 3: Backend & Deployment */}
              <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 hover:border-cyan-500/50 hover:shadow-lg hover:shadow-cyan-900/20 hover:-translate-y-1 transition-all duration-300 group">
                <div className="flex items-center gap-3 mb-6 border-b border-slate-700 pb-4">
                  <div className="p-2.5 bg-cyan-500/10 rounded-lg text-cyan-400 group-hover:scale-110 transition-transform"><Terminal size={22}/></div>
                  <h4 className="font-bold text-white text-lg">Backend & Deployment</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {["FastAPI", "Flask", "Docker", "Streamlit", "REST APIs", "Git"].map((skill) => (
                    <span key={skill} className="px-3 py-1.5 bg-slate-900 text-slate-300 rounded-lg text-xs font-semibold hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors cursor-default">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {/* Card 4: Programming */}
              <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 hover:border-cyan-500/50 hover:shadow-lg hover:shadow-cyan-900/20 hover:-translate-y-1 transition-all duration-300 group">
                <div className="flex items-center gap-3 mb-6 border-b border-slate-700 pb-4">
                  <div className="p-2.5 bg-cyan-500/10 rounded-lg text-cyan-400 group-hover:scale-110 transition-transform"><Terminal size={22}/></div>
                  <h4 className="font-bold text-white text-lg">Programming</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {["Python", "SQL", "C++", "JavaScript", "HTML/CSS"].map((skill) => (
                    <span key={skill} className="px-3 py-1.5 bg-slate-900 text-slate-300 rounded-lg text-xs font-semibold hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors cursor-default">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {/* Card 5: Databases & Data Pipelines */}
              <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 hover:border-cyan-500/50 hover:shadow-lg hover:shadow-cyan-900/20 hover:-translate-y-1 transition-all duration-300 group">
                <div className="flex items-center gap-3 mb-6 border-b border-slate-700 pb-4">
                  <div className="p-2.5 bg-cyan-500/10 rounded-lg text-cyan-400 group-hover:scale-110 transition-transform"><Database size={22}/></div>
                  <h4 className="font-bold text-white text-lg">Data & Pipelines</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {["MySQL", "Vector Embeddings", "ETL Pipelines", "Power BI", "Data Cleaning"].map((skill) => (
                    <span key={skill} className="px-3 py-1.5 bg-slate-900 text-slate-300 rounded-lg text-xs font-semibold hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors cursor-default">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {/* Card 6: Core Computer Science */}
              <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 hover:border-cyan-500/50 hover:shadow-lg hover:shadow-cyan-900/20 hover:-translate-y-1 transition-all duration-300 group">
                <div className="flex items-center gap-3 mb-6 border-b border-slate-700 pb-4">
                  <div className="p-2.5 bg-cyan-500/10 rounded-lg text-cyan-400 group-hover:scale-110 transition-transform"><Search size={22}/></div>
                  <h4 className="font-bold text-white text-lg">Core Engineering</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                    {["Data Structures", "Algorithms", "DBMS", "OOPs", "System Architecture"].map((skill) => (
                      <span key={skill} className="px-3 py-1.5 bg-slate-900 text-slate-300 rounded-lg text-xs font-semibold hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors cursor-default">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          <FloatingSkills />
        </section>
       
        {/* Certifications Section */}
        <section id="certifications" className="py-24 bg-[#0f172a]">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-center gap-4 mb-16">
              <h2 className="text-3xl font-bold text-white"><span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent font-mono pr-2">02.</span> Certifications</h2>
              <div className="h-px bg-slate-800 flex-grow"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {certifications.map((cert, index) => (
                <a key={index} href={cert.link ? cert.link : "#"} target={cert.link ? "_blank" : "_self"} rel="noopener noreferrer" className={`block bg-slate-800 p-8 rounded-2xl border border-slate-700 transition-all group h-full flex flex-col ${cert.link ? 'hover:border-cyan-500/40 hover:-translate-y-2 hover:shadow-xl hover:shadow-cyan-900/10 cursor-pointer' : 'cursor-default'}`}>
                  <div className="flex justify-between items-start mb-6">
                    <div className="p-3 bg-cyan-500/10 rounded-xl text-cyan-400 group-hover:bg-cyan-500 group-hover:text-white transition-colors"><Award size={28} /></div>
                    {cert.link && <ExternalLink size={20} className="text-slate-600 group-hover:text-cyan-400 transition-colors" />}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">{cert.title}</h3>
                  <div className="flex items-center justify-between mb-6">
                      <p className="text-sm text-cyan-500 font-bold">{cert.issuer}</p>
                      <span className="text-xs font-mono text-slate-500 border border-slate-700 px-2 py-1 rounded-md">{cert.date}</span>
                  </div>
                  <ul className="space-y-3 flex-grow border-t border-slate-700/50 pt-4">
                    {cert.points.map((point, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-400 leading-relaxed">
                        <span className="mt-1.5 min-w-[6px] h-[6px] rounded-full bg-cyan-500/50 group-hover:bg-cyan-400"></span>{point}
                      </li>
                    ))}
                  </ul>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section id="projects" className="py-24 bg-slate-900/50 border-t border-slate-800">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-center gap-4 mb-16">
              <h2 className="text-3xl font-bold text-white">
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent font-mono pr-2">03.</span> Featured Projects</h2>
              <div className="h-px bg-slate-800 flex-grow"></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {projects.map((project, index) => (
                <div key={index} className="group bg-[#0f172a] rounded-2xl border border-slate-800 hover:border-cyan-500/50 transition-all hover:-translate-y-2 duration-300 flex flex-col shadow-lg overflow-hidden">
                  <div className="p-8 pb-0 flex-grow">
                    <div className="flex justify-between items-start mb-6">
                      <div className="p-3 bg-cyan-500/10 rounded-xl text-cyan-400 group-hover:scale-110 transition-transform">
                        {project.category === 'ai' || project.category === 'ml' ? <Brain size={28} /> : <Database size={28} />}
                      </div>
                      <a href={project.links.code} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-white transition-colors" title="View Source Code">
                        <Github size={24} />
                      </a>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">{project.title}</h3>
                    <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-8">
                      {project.tags.map(tag => (
                        <span key={tag} className="text-xs font-mono text-cyan-300 bg-cyan-900/20 border border-cyan-900/50 px-3 py-1.5 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="mt-auto flex border-t border-slate-800">
                    <a href={project.links.tryIt} className="flex-1 text-center py-4 text-sm font-bold text-white bg-slate-800/30 hover:bg-cyan-600 transition-colors flex items-center justify-center gap-2">
                      Try It Live <ExternalLink size={16} />
                    </a>
                    <div className="w-px bg-slate-800"></div>
                    <button onClick={(e) => openModal(e, project)} className="flex-1 text-center py-4 text-sm font-bold text-cyan-400 hover:bg-slate-800 transition-colors flex items-center justify-center gap-2">
                      Learn More <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-32 max-w-2xl mx-auto px-6 text-center">
          <p className="text-cyan-400 font-mono mb-4">04. What's Next?</p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Get In Touch</h2>
          <p className="text-slate-400 text-lg mb-10 leading-relaxed">
              Interested in discussing scalable AI architectures, generative systems, or potential collaborations? My inbox is always open!
          </p>
          <form ref={form} onSubmit={sendEmail} className="text-left bg-slate-800/50 backdrop-blur-sm p-8 rounded-3xl border border-slate-700 mb-12 shadow-xl">
            <div className="mb-6">
              <label className="block text-sm font-bold text-slate-300 mb-2">Your Name</label>
              <input type="text" name="user_name" required className="w-full bg-slate-900 border border-slate-700 rounded-xl p-4 text-white focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-all" placeholder="Subhankar Chand" />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-bold text-slate-300 mb-2">Email Address</label>
              <input type="email" name="user_email" required className="w-full bg-slate-900 border border-slate-700 rounded-xl p-4 text-white focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-all" placeholder="subhankar_example@gmail.com" />
            </div>
            <div className="mb-8">
              <label className="block text-sm font-bold text-slate-300 mb-2">Message</label>
              <textarea name="message" required className="w-full bg-slate-900 border border-slate-700 rounded-xl p-4 text-white h-32 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-all resize-none" placeholder="Hello, I'd like to discuss..." />
            </div>
            <button type="submit" className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-bold hover:shadow-lg hover:shadow-cyan-500/25 transition-all flex items-center justify-center gap-2 text-lg">
              <Send size={20} /> Send Message
            </button>
          </form>
          <div className="flex justify-center gap-8 mb-12">
              <a href="https://github.com/SubhankarChand" target="_blank" rel="noopener noreferrer" className="p-3 bg-slate-800 rounded-full text-slate-400 hover:text-white hover:bg-cyan-500 transition-all hover:-translate-y-1"><Github size={24} /></a>
              <a href="https://www.linkedin.com/in/subhankarchand/" target="_blank" rel="noopener noreferrer" className="p-3 bg-slate-800 rounded-full text-slate-400 hover:text-white hover:bg-cyan-500 transition-all hover:-translate-y-1"><Linkedin size={24} /></a>
              <a href="mailto:subhankarchand66@gmail.com" className="p-3 bg-slate-800 rounded-full text-slate-400 hover:text-white hover:bg-cyan-500 transition-all hover:-translate-y-1"><Mail size={24} /></a>
          </div>
          {/* REMOVED: Redundant Designed & Built Text */}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Portfolio;